import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AttendanceException,
  BulkResolveBody,
  CreateAdjustmentBody,
  ProcessAttendanceBody,
  QuickResolveBody,
  ResolveExceptionBody,
} from '@mervonn/shared-types';
import {
  attendanceApi,
  EXCEPTIONS_PER_PAGE,
  type ExceptionsFilter,
  type RawEventsFilter,
  type RecordsFilter,
} from './api';

const keys = {
  all: ['attendance'] as const,
  records: (f: RecordsFilter) => ['attendance', 'records', f] as const,
  exceptions: (f: ExceptionsFilter) => ['attendance', 'exceptions', f] as const,
  rawEvents: (f: RawEventsFilter) => ['attendance', 'raw-events', f] as const,
};

export function useAttendanceRecords(f: RecordsFilter, enabled = true) {
  return useQuery({ queryKey: keys.records(f), queryFn: () => attendanceApi.records(f), enabled });
}

/** One page of processed records with the total, for the Records tab. */
export function useAttendanceRecordsPage(f: RecordsFilter & { page: number; per_page: number }, enabled = true) {
  return useQuery({
    queryKey: ['attendance', 'records-page', f] as const,
    queryFn: () => attendanceApi.recordsPage(f),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useRawEventsPage(f: RawEventsFilter & { page: number; per_page: number }, enabled: boolean) {
  return useQuery({
    queryKey: ['attendance', 'raw-events-page', f] as const,
    queryFn: () => attendanceApi.rawEventsPage(f),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useAttendanceExceptions(f: ExceptionsFilter, enabled = true) {
  return useQuery({
    queryKey: keys.exceptions(f),
    queryFn: () => attendanceApi.exceptions(f),
    enabled,
  });
}

export function useAttendanceExceptionsPage(f: ExceptionsFilter, enabled = true, perPage: number = EXCEPTIONS_PER_PAGE) {
  return useQuery({
    queryKey: ['attendance', 'exceptions-page', f, perPage] as const,
    queryFn: () => attendanceApi.exceptionsPage(f, perPage),
    enabled,
    placeholderData: (prev) => prev,
  });
}

/** Attendance KPI row (Stage 7 Phase 2A) — from processed records. */
export function useAttendanceSummary(enabled = true, branchId?: string | null, range?: { from: string; to: string }) {
  return useQuery({
    queryKey: ['attendance', 'summary', branchId ?? 'all', range?.from ?? '', range?.to ?? ''],
    queryFn: () => attendanceApi.summary(undefined, branchId, range),
    enabled,
    staleTime: 30_000,
  });
}

export function useRawEvents(f: RawEventsFilter, enabled: boolean) {
  return useQuery({ queryKey: keys.rawEvents(f), queryFn: () => attendanceApi.rawEvents(f), enabled });
}

/** `00-dashboard-implementation-plan.md` Phase 6.3 — "Active Shift" badge. */
export function useActiveShift(enabled = true) {
  return useQuery({
    queryKey: ['attendance', 'active-shift'],
    queryFn: () => attendanceApi.activeShift(),
    enabled,
    staleTime: 30_000,
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: keys.all });
}

export function useProcessAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProcessAttendanceBody) => attendanceApi.process(body),
    onSuccess: () => invalidate(qc),
  });
}

export function useAddAdjustment(recordId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAdjustmentBody) => attendanceApi.addAdjustment(recordId, body),
    onSuccess: () => invalidate(qc),
  });
}

export function useReviewException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => attendanceApi.reviewException(id),
    onSuccess: () => invalidate(qc),
  });
}

export function useResolveException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ResolveExceptionBody }) =>
      attendanceApi.resolveException(id, body),
    onSuccess: () => invalidate(qc),
  });
}

type ExceptionsSnapshot = [readonly unknown[], AttendanceException[] | undefined][];

/** Drop `ids` from every cached exceptions list (the dashboard table, the Exceptions tab). */
async function removeFromCachedExceptions(qc: ReturnType<typeof useQueryClient>, ids: string[]): Promise<ExceptionsSnapshot> {
  const queryKey = ['attendance', 'exceptions'] as const;
  await qc.cancelQueries({ queryKey });
  const snapshot = qc.getQueriesData<AttendanceException[]>({ queryKey });
  const gone = new Set(ids);
  qc.setQueriesData<AttendanceException[]>({ queryKey }, (old) => old?.filter((e) => !gone.has(e.exception_id)));
  return snapshot;
}

function restore(qc: ReturnType<typeof useQueryClient>, snapshot: ExceptionsSnapshot | undefined) {
  for (const [key, data] of snapshot ?? []) qc.setQueryData(key, data);
}

/** Settled: re-read the truth, including what the dashboard shows derived from exceptions. */
function invalidateAfterResolve(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: keys.all }),
    qc.invalidateQueries({ queryKey: ['dashboard'] }),
  ]);
}

/** Re-design T17 — one-click resolve with optimistic removal and rollback. */
export function useQuickResolveException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: QuickResolveBody }) => attendanceApi.quickResolveException(id, body),
    onMutate: ({ id }) => removeFromCachedExceptions(qc, [id]),
    onError: (_err, _vars, snapshot) => restore(qc, snapshot),
    onSettled: () => invalidateAfterResolve(qc),
  });
}

/** Re-design T17 — "Excuse all late". Per-item failures come back in the result, not as an error. */
export function useBulkResolveExceptions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkResolveBody) => attendanceApi.bulkResolveExceptions(body),
    onMutate: (body) => removeFromCachedExceptions(qc, body.exception_ids),
    onError: (_err, _vars, snapshot) => restore(qc, snapshot),
    onSettled: () => invalidateAfterResolve(qc),
  });
}
